import { yupResolver } from "@hookform/resolvers/yup";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import { Autocomplete, Grid, InputAdornment, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { postData } from "../../Services/postData";
import { FormsIds, PaymentMethods, PaymentType } from "../../Utils/Constants";
import { WithdrawMoneySchema } from "../../Utils/Models";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponents";
import { WithdrawMoneyFormProps } from "../../Utils/Types";
import { endpoints } from "../../Utils/UrlEnum";
import { onFormError } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";

export default function WithdrawMoneyForm(props: WithdrawMoneyFormProps) {
  const memo = useCustomMemo();
  const bankAccounts = memo?.storage.bankAccounts;
  const { onClose } = props;

  const onSubmit = async (data: any) => {
    data.createdByUserId = localStorage.getItem("userId");
    data.paymentType = PaymentType.WITHDRAW;
    data.paymentMethod = PaymentMethods.CASH;
    data.accountId = data.accountId?._id;

    const url = `${endpoints.payments.main}`;
    const res = (await postData(url, data)) as any;
    if (!res || res?.error) {
      return false;
    }
    onClose();
    return true;
  };

  /**
   * Create the form
   */
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(WithdrawMoneySchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  return (
    <div>
      <form
        id={FormsIds.withdrawMoneyForm}
        onSubmit={handleSubmit(onSubmit, onFormError)}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name={"amount"}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="amount"
                  id="amount"
                  type="text"
                  label={Vocabulary.amountWithdraw}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalAtmIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {Vocabulary.currency}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="accountId"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  disablePortal
                  disableClearable
                  clearOnBlur={true}
                  options={bankAccounts ?? []}
                  getOptionLabel={(option: any) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.name == value?.name
                  }
                  value={field.value}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={field?.value || " "}
                      label={Vocabulary.account}
                      error={!!error}
                      helperText={error?.message}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name={"description"}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="description"
                  id="description"
                  label={Vocabulary.description}
                  fullWidth
                  multiline
                  rows={4}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </div>
  );
}
